import { ReactNode, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ParseResult } from "@equipped/core/core/result";
import { FieldForm } from "../FieldForm";
import { RenderField } from "../fieldTypes";

/**
 * The shared chrome around a Catalog or Pack DOCUMENT's properties panel (docs/343
 * v0 §2/§7): a Fields/Raw-JSON tab switch, the real-parser validation result
 * (blocking Save on failure), and Save/Discard-changes buttons. `extra` renders
 * document-specific content below the fields (the entries/lessons tree summary
 * lives in the tree itself, not here — but a panel can still show e.g. a lesson
 * count).
 */
export function DocumentPanel({
  title,
  fields,
  value,
  onChange,
  onSave,
  onDiscard,
  dirty,
  saving,
  saveError,
  validate,
  onBrowseFile,
  extra,
}: {
  title: string;
  fields: RenderField[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  onSave: () => void;
  onDiscard: () => void;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  validate: (draft: unknown) => ParseResult<unknown>;
  onBrowseFile?: (fieldKey: string) => void;
  extra?: ReactNode;
}) {
  const [tab, setTab] = useState<"fields" | "json">("fields");
  const [jsonText, setJsonText] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const verdict = useMemo(() => validate(value), [validate, value]);

  const openJsonTab = () => {
    setJsonText(JSON.stringify(value, null, 2));
    setJsonError(null);
    setTab("json");
  };
  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText ?? "{}");
      onChange(parsed);
      setJsonError(null);
      setTab("fields");
    } catch {
      setJsonError("Invalid JSON — not applied.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ mb: 0 }}>
          {title}
        </Typography>
        {dirty ? <Chip label="Unsaved changes" size="small" color="warning" /> : null}
      </Box>

      <Tabs value={tab} onChange={(_, v) => (v === "json" ? openJsonTab() : setTab(v))} sx={{ mb: 2 }}>
        <Tab value="fields" label="Fields" />
        <Tab value="json" label="Raw JSON" />
      </Tabs>

      {tab === "fields" ? (
        <>
          {!verdict.ok ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {verdict.errors.join("; ")}
            </Alert>
          ) : null}
          <FieldForm fields={fields} value={value} onChange={onChange} onBrowseFile={onBrowseFile} />
          {extra}
        </>
      ) : (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={16}
            value={jsonText ?? ""}
            onChange={(e) => setJsonText(e.target.value)}
            sx={{ fontFamily: "monospace", mb: 1 }}
          />
          {jsonError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {jsonError}
            </Alert>
          ) : null}
          <Button variant="outlined" onClick={applyJson}>
            Apply JSON
          </Button>
        </Box>
      )}

      {saveError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {saveError}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
        <Button variant="contained" onClick={onSave} disabled={saving || !verdict.ok}>
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
        <Button variant="outlined" color="secondary" onClick={onDiscard} disabled={saving || !dirty}>
          Discard changes
        </Button>
      </Box>
    </Box>
  );
}
